import { brandingApi } from '@/pages/branding/brandingApi.ts'
import { CameraOutlined, DeleteOutlined } from '@ant-design/icons'
import { Button, Flex, Spin, Upload } from 'antd'
import ImgCrop from 'antd-img-crop'
import { RcFile } from 'antd/es/upload'
import { customizationApi } from 'common/api/entitiesApi.tsx'
import { processError } from 'common/api/fetch.ts'
import { FormCard } from 'common/components/FormCard'
import { useAppContext } from 'common/context/AppContext.tsx'
import { userUpdateStore } from 'common/util/userUpdateStore.tsx'
import { showMessage } from 'common/util/Util.tsx'
import React, { useMemo, useState } from 'react'

type BrandingImageType = 'logo' | 'favicon'

export interface SettingsLogoProps {
  type?: BrandingImageType
}

type Config = {
  title: string
  description: React.ReactNode
  uploadSuccessText: string
  uploadErrorText: string
  removeSuccessText: string
  removeErrorText: string
  maxSizeKb: number
  accept: string
  allowCrop: boolean
  cropAspect?: number
  cropModalTitle?: string
  cropModalOk?: string
}

const isPng = (file: RcFile) => file.type === 'image/png' || file.name.toLowerCase().endsWith('.png')
const isIco = (file: RcFile) =>
  file.type === 'image/x-icon' ||
  file.type === 'image/vnd.microsoft.icon' ||
  file.name.toLowerCase().endsWith('.ico')

export const SettingsLogo: React.FC<SettingsLogoProps> = ({ type = 'logo' }) => {
  const [isUploading, setIsUploading] = useState(false)
  const { messageApi } = useAppContext()

  const cfg: Config = useMemo(() => {
    if (type === 'favicon') {
      return {
        title: 'Favicon',
        description: (
          <>
            Upload a favicon for your agency interface. Recommended size: 64x64 (square). PNG or ICO, no larger than 64KB.
          </>
        ),
        uploadSuccessText: 'Favicon was successfully uploaded',
        uploadErrorText: 'Favicon was not uploaded',
        removeSuccessText: 'Favicon was successfully removed',
        removeErrorText: 'Favicon was not removed',
        maxSizeKb: 96,
        accept: 'image/png,image/x-icon,image/vnd.microsoft.icon,.png,.ico',
        allowCrop: false,
      }
    }

    return {
      title: 'Logo',
      description: (
        <>
          Upload a logo to brand your agency interface. We recommend using at least a 300x200px (2:1 ratio), no larger than 128KB.
        </>
      ),
      uploadSuccessText: 'Logo was successfully uploaded',
      uploadErrorText: 'Logo was not uploaded',
      removeSuccessText: 'Logo was successfully removed',
      removeErrorText: 'Logo was not removed',
      maxSizeKb: 192,
      accept: 'image/png,.png',
      allowCrop: true,
      cropAspect: 4,
      cropModalTitle: 'Add logo',
      cropModalOk: 'Save',
    }
  }, [type])

  function validateFile(file: RcFile): boolean {
    const sizeKb = file.size / 1024
    if (sizeKb > cfg.maxSizeKb) {
      showMessage(messageApi, 'Error', `Image must be smaller than ${cfg.maxSizeKb}KB`)
      return false
    }

    if (type === 'logo') {
      if (!isPng(file)) {
        showMessage(messageApi, 'Error', 'Logo must be a PNG image')
        return false
      }
      return true
    }

    // favicon
    if (!isPng(file) && !isIco(file)) {
      showMessage(messageApi, 'Error', 'Favicon must be a PNG or ICO image')
      return false
    }
    return true
  }

  function upload(file: RcFile) {
    if (!validateFile(file)) return false

    setIsUploading(true)

    const p =
      type === 'favicon'
        ? brandingApi.uploadFavicon(file)
        : brandingApi.uploadLogo(file)

    p.then(() => {
      showMessage(messageApi, 'Success', cfg.uploadSuccessText)
      userUpdateStore.updateInfo()
    })
      .catch(() => showMessage(messageApi, 'Error', cfg.uploadErrorText))
      .finally(() => setIsUploading(false))

    return false
  }

  function remove() {
    setIsUploading(true)

    const p =
      type === 'favicon'
        ? customizationApi.deleteFavicon()
        : customizationApi.deleteLogo()

    p.then((response) => {
      if (!response.ok) throw response
      showMessage(messageApi, 'Success', cfg.removeSuccessText)
      userUpdateStore.updateInfo()
      return Promise.resolve()
    })
      .catch((err) => {
        // сохраняем вашу текущую схему обработки ошибок
        processError(err)
        showMessage(messageApi, 'Error', cfg.removeErrorText)
      })
      .finally(() => setIsUploading(false))

    return false
  }

  const uploadButton = (
    <Upload beforeUpload={upload} accept={cfg.accept} showUploadList={false} disabled={isUploading}>
      <Button type="primary" disabled={isUploading} className="adsp-button" htmlType="button">
        <CameraOutlined /> Upload {cfg.title.toLowerCase()}
        {isUploading && <Spin />}
      </Button>
    </Upload>
  )

  return (
    <FormCard title={cfg.title}>
      <p style={{ fontSize: 16 }}>{cfg.description}</p>

      <Flex style={{ marginTop: 30, marginRight: 24, marginLeft: 16 }}>
        <Flex gap="middle" justify="flex-start" align="flex-start">
          {cfg.allowCrop ? (
            <ImgCrop
              quality={1}
              fillColor="transparent"
              aspect={cfg.cropAspect}
              modalTitle={cfg.cropModalTitle}
              modalOk={cfg.cropModalOk}
            >
              {uploadButton}
            </ImgCrop>
          ) : (
            uploadButton
          )}

          <Button
            type="primary"
            disabled={isUploading}
            className="adsp-button"
            htmlType="button"
            ghost
            onClick={remove}
          >
            <DeleteOutlined /> Remove {cfg.title.toLowerCase()}
            {isUploading && <Spin />}
          </Button>
        </Flex>
      </Flex>
    </FormCard>
  )
}
